/**
 * Active Session Service
 *
 * Manages the mapping between permanent profileUserId and current chatRoomId.
 * Used for the permanent URL QR code system.
 *
 * Flow:
 * 1. Profile user opens QR screen → activateSession(profileUserId, chatRoomId)
 * 2. Anon scans QR → getActiveSession(profileUserId) → returns chatRoomId
 * 3. Profile user leaves QR screen → deactivateSession(profileUserId)
 */

import { supabase, getAuthUserId, authReady } from './supabase';
import { logConnectionError } from './analytics/eventLogger';
import { SOURCE_BRANCH } from '../utils/deployInfo';
import { SESSION_FRESHNESS_MS } from '../utils/lifecycleWindows';

// Re-exported so session-freshness consumers (QRScreen, tests) can import it
// alongside the session functions that enforce it.
export { SESSION_FRESHNESS_MS };

// Connections older than this are considered stale (from a previous exchange).
// Legitimate mid-connection anon_connected_at is < 5s old; minimum time from
// completed exchange back to QR screen is > 10s. Exported: qrDetection uses
// the same freshness line to tell a live re-scan from a stale ghost mark.
export const STALE_THRESHOLD_MS = 10000;

/**
 * Activate a session for a Profile user.
 * Called when Profile user opens QR screen.
 *
 * Uses upsert so:
 * - First time: creates new row
 * - Subsequent times: overwrites with new chatRoomId
 *
 * @param {string} profileUserId - The permanent profile user ID (nanoid)
 * @param {string} chatRoomId - The current chat room ID for this session
 * @returns {Promise<object>} - The upserted session data
 * @throws {Error} - If Supabase operation fails
 */
export const activateSession = async (profileUserId, chatRoomId) => {
  // Check if supabase has database methods (not mock client)
  if (!supabase.from) {
    console.warn('[ActiveSession] Supabase client not initialized - skipping activation');
    return null;
  }

  // Guard: check if an Anon is mid-connection on the existing session.
  // If so, return the existing session instead of overwriting —
  // prevents orphaning an Anon who already connected.
  // Note: does NOT check is_active — cleanup sets is_active=false before
  // Profile reopens QR, but anon_connected persists until detection resets it.
  const { data: existing } = await supabase
    .from('active_sessions')
    .select('profile_user_id, chat_room_id, is_active, anon_connected, anon_connected_at')
    .eq('profile_user_id', profileUserId)
    .eq('anon_connected', true)
    .maybeSingle();

  if (existing) {
    const ageMs = existing.anon_connected_at
      ? Date.now() - new Date(existing.anon_connected_at).getTime()
      : null;
    const isStale = ageMs === null || ageMs > STALE_THRESHOLD_MS;
    console.log('[ActiveSession] Guard: existing connection found', {
      profileUserId,
      existingChatRoomId: existing.chat_room_id,
      requestedChatRoomId: chatRoomId,
      anon_connected_at: existing.anon_connected_at,
      ageMs,
      decision: isStale
        ? ageMs === null
          ? 'stale (null timestamp) → overwriting'
          : 'stale (>10s) → overwriting'
        : 'protecting (≤10s) → returning existing',
    });

    if (!isStale) {
      // Anon connected recently — protect this session
      await supabase
        .from('active_sessions')
        .update({ is_active: true, updated_at: new Date().toISOString() })
        .eq('profile_user_id', profileUserId);
      return [{ ...existing, is_active: true }];
    }
    // Stale connection — fall through to normal upsert
  }

  const { data, error } = await supabase
    .from('active_sessions')
    .upsert(
      {
        profile_user_id: profileUserId,
        chat_room_id: chatRoomId,
        is_active: true,
        anon_connected: false,
        anon_connected_at: null,
        returning_room_id: null,
        // A fresh room is unclaimed (052): the next scanner can take it
        claimed_by_auth: null,
        claimed_at: null,
        updated_at: new Date().toISOString(),
        auth_user_id: getAuthUserId(),
      },
      {
        onConflict: 'profile_user_id',
      }
    )
    .select();

  if (error) {
    console.error('[ActiveSession] Failed to activate:', error);
    logConnectionError('database', {
      operation: 'activate',
      code: error.code,
      message: error.message || 'Database activation failed',
      profileUserId,
      chatRoomId,
    });
    throw error;
  }

  console.log('[ActiveSession] Activated:', { profileUserId, chatRoomId });
  return data;
};

/**
 * Deactivate a session for a Profile user.
 * Called when Profile user leaves QR screen.
 *
 * Note: This is optional - upsert overwrites anyway.
 * But it's good practice to mark sessions inactive for:
 * - Faster "Not Available" responses for Anon users
 * - Future analytics on session duration
 *
 * @param {string} profileUserId - The permanent profile user ID
 * @returns {Promise<void>}
 */
export const deactivateSession = async (profileUserId) => {
  // Check if supabase has database methods (not mock client)
  if (!supabase.from) {
    console.warn('[ActiveSession] Supabase client not initialized - skipping deactivation');
    return;
  }

  const { error } = await supabase
    .from('active_sessions')
    .update({
      is_active: false,
      anon_connected: false,
      anon_connected_at: null,
      claimed_by_auth: null,
      claimed_at: null,
      updated_at: new Date().toISOString(),
    })
    .eq('profile_user_id', profileUserId);

  if (error) {
    console.error('[ActiveSession] Failed to deactivate:', error);
    logConnectionError('database', {
      operation: 'deactivate',
      code: error.code,
      message: error.message || 'Database deactivation failed',
      profileUserId,
    });
    // Don't throw - deactivation failure shouldn't break the app
    return;
  }

  console.log('[ActiveSession] Deactivated:', { profileUserId });
};

/**
 * Touch a session to update its timestamp (keep-alive heartbeat).
 * Called periodically while QR screen is open to prevent staleness.
 *
 * Sessions with updated_at older than SESSION_FRESHNESS_MS (5 min) are
 * rejected by both the prefetch fast path (public/index.html) and
 * getActiveSession. This heartbeat runs every 2 minutes to stay inside it.
 *
 * @param {string} profileUserId - The permanent profile user ID
 * @returns {Promise<boolean>} - true if touch succeeded, false if failed
 */
export const touchSession = async (profileUserId) => {
  if (!supabase.from) {
    console.warn('[ActiveSession] Supabase client not initialized - skipping touch');
    return false;
  }

  const { error } = await supabase
    .from('active_sessions')
    .update({ updated_at: new Date().toISOString() })
    .eq('profile_user_id', profileUserId)
    .eq('is_active', true);

  if (error) {
    console.error('[ActiveSession] Failed to touch session:', error);
    return false;
  }

  console.log('[ActiveSession] Touched session:', { profileUserId });
  return true;
};

/**
 * The signed-in session's bearer token for a raw-fetch RPC. null when there is no session.
 * @param {string} caller - for the log line
 */
export const getSessionBearer = async (caller) => {
  try {
    await authReady;
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.access_token || null;
  } catch (err) {
    console.warn(`[ActiveSession] Failed to get session for ${caller}:`, err.message);
    return null;
  }
};

const SCANNER_RPC_RETRIES = 3;
const SCANNER_RPC_BACKOFF_MS = [0, 1000, 2000];

/**
 * A scanner-side server call over raw fetch — Android Chrome stalls Supabase REST calls
 * while a WebSocket to the same host is connecting; raw fetch avoids the SDK's pooling.
 * Signed-in sessions only (open-access lockdown: no public-key fallback). Retries with
 * backoff on HTTP or network failure; logs the final failure under `operation`.
 *
 * @param {string} fn - the RPC name
 * @param {Object} body - its arguments
 * @param {Object} log - { operation, chatRoomId } for the error log
 * @returns {Promise<{ok: true, data}|{ok: false}>}
 */
export const postScannerRpc = async (fn, body, { operation, chatRoomId }) => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
  if (!supabaseUrl || !supabaseKey) {
    console.warn(`[ActiveSession] Missing Supabase config - skipping ${fn}`);
    return { ok: false };
  }
  const authToken = await getSessionBearer(fn);
  if (!authToken) {
    logConnectionError('database', {
      operation,
      code: 'NO_SESSION',
      message: 'No signed-in session — cannot mark the host session',
      chatRoomId,
    });
    return { ok: false };
  }

  for (let attempt = 0; attempt < SCANNER_RPC_RETRIES; attempt++) {
    if (attempt > 0) {
      await new Promise((r) => setTimeout(r, SCANNER_RPC_BACKOFF_MS[attempt]));
      console.log(`[ActiveSession] ${fn} retry ${attempt}/${SCANNER_RPC_RETRIES - 1}`);
    }
    const last = attempt === SCANNER_RPC_RETRIES - 1;
    try {
      const response = await fetch(`${supabaseUrl}/rest/v1/rpc/${fn}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: supabaseKey,
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        console.error(`[ActiveSession] ${fn} failed:`, response.status);
        if (last) {
          logConnectionError('database', {
            operation,
            code: `HTTP_${response.status}`,
            message: `HTTP ${response.status} after ${SCANNER_RPC_RETRIES} attempts`,
            chatRoomId,
          });
        }
        continue; // Retry
      }
      return { ok: true, data: await response.json() };
    } catch (error) {
      console.error(`[ActiveSession] ${fn} fetch error:`, error);
      if (last) {
        logConnectionError('database', {
          operation,
          code: 'FETCH_ERROR',
          message: `${error.message || 'Fetch failed'} after ${SCANNER_RPC_RETRIES} attempts`,
          chatRoomId,
        });
      }
    }
  }
  return { ok: false };
};

/**
 * Claim a host's live QR room before writing anything into it (052): one
 * exchange per code at a time. The server takes the first caller in one
 * conditional update, so of two scanners released together exactly one wins.
 * The same signed-in session claiming again (a reload) still wins.
 *
 * @param {string} chatRoomId - the room the host's live code points at
 * @returns {Promise<'claimed'|'busy'|'closed'|null>} 'busy' = someone else is
 *   mid-exchange on this code; 'closed' = the code isn't open; null = the call
 *   failed (logged by postScannerRpc)
 */
export const claimQrRoom = async (chatRoomId) => {
  const result = await postScannerRpc(
    'claim_qr_room',
    { p_chat_room_id: chatRoomId },
    { operation: 'claim_qr_room', chatRoomId }
  );
  return result.ok ? result.data : null;
};

/**
 * Mark a session as having an Anon user connected.
 * Called by Anon side after successfully subscribing to the channel.
 * This is the guaranteed database signal that Profile polls for.
 *
 * Retries with backoff (postScannerRpc). Filters on is_active=true — a mark landing on a
 * deactivated session must match zero rows, not resurrect it (a stale
 * anon_connected=true triggers ghost detection that re-runs formation and
 * rewrites origin data). The call answers whether a live session matched: zero rows
 * means the row was overwritten by a new session, or the session is no longer active.
 *
 * @param {string} chatRoomId - The chat room ID for this session
 * @returns {Promise<boolean>} - true if update succeeded, false if failed
 */
export const markAnonConnected = async (chatRoomId) => {
  const result = await postScannerRpc(
    'mark_anon_connected',
    { p_chat_room_id: chatRoomId },
    { operation: 'mark_anon_connected', chatRoomId }
  );
  if (!result.ok) return false;

  // false = no live session matched (the server's is_active filter)
  if (result.data !== true) {
    // Two causes: row overwritten by a new session (Profile re-entered
    // QRScreen — recovery via connections table handles it), or session
    // inactive (ghost mark on a dead session — the is_active filter made
    // it a no-op). Log for monitoring; ghost attempts surface here.
    console.warn(
      '[ActiveSession] markAnonConnected matched zero rows — row overwritten or session inactive:',
      chatRoomId
    );
    logConnectionError('database', {
      operation: 'mark_anon_connected',
      code: 'ZERO_ROWS',
      message:
        'No active session row — overwritten by a new session or session inactive (ghost mark); recovery via connections table if needed',
      chatRoomId,
    });
    return false; // No point retrying — no active row to mark
  }

  console.log('[ActiveSession] Marked anon connected for chatRoomId:', chatRoomId);
  return true;
};

/**
 * Check if an Anon user has connected to a session.
 * Used by Profile's polling fallback to detect Anon connection.
 *
 * @param {string} chatRoomId - The chat room ID to check
 * @returns {Promise<boolean>} - true if anon_connected is true
 */
export const checkAnonConnected = async (chatRoomId) => {
  if (!supabase.from) {
    return false;
  }

  const { data, error } = await supabase
    .from('active_sessions')
    .select('anon_connected, anon_connected_at, returning_room_id')
    .eq('chat_room_id', chatRoomId)
    .maybeSingle();

  if (error) {
    console.error('[ActiveSession] Failed to check anon connected:', error);
    return false;
  }

  if (!data?.anon_connected) return false;
  return {
    connected: true,
    anon_connected_at: data.anon_connected_at,
    // A known guest re-scanning: the room they already share (mark_guest_returned)
    returning_room_id: data.returning_room_id || null,
  };
};

/**
 * Reset the anon-connected mark for a Profile user's session.
 * Called after detection handles (or rejects) a mark — without the reset,
 * anon_connected=true persists and captures the next QR open via the
 * activateSession protect-guard. Fire-and-forget; errors are logged here.
 *
 * @param {string} profileUserId - The permanent profile user ID
 * @returns {Promise<void>}
 */
export const resetAnonConnected = async (profileUserId) => {
  const { error } = await supabase
    .from('active_sessions')
    .update({ anon_connected: false, anon_connected_at: null, returning_room_id: null })
    .eq('profile_user_id', profileUserId);

  if (error) console.error('[ActiveSession] Failed to reset anon_connected:', error);
};

/**
 * Look up active chatRoomId for a Profile user.
 * Called when Anon user scans QR code.
 *
 * @param {string} profileUserId - The permanent profile user ID from URL
 * @returns {Promise<object|null>} - Session data with chat_room_id, or null if not active
 */
export const getActiveSession = async (profileUserId) => {
  // Check if supabase has database methods (not mock client)
  if (!supabase.from) {
    console.error('[ActiveSession] Supabase client not initialized - cannot lookup session');
    return null;
  }

  // The host's live QR through get_qr_landing (open-access lockdown: a
  // stranger's session row is no longer readable directly). maybeSingle: no
  // profile is data null; a profile with no live QR has a null room.
  const { data: landing, error } = await supabase
    .rpc('get_qr_landing', { p_profile_user_id: profileUserId })
    .maybeSingle();
  const data = landing?.chat_room_id
    ? {
        chat_room_id: landing.chat_room_id,
        created_at: landing.session_created_at,
        updated_at: landing.session_updated_at,
        // For the busy message ("<first name>'s connecting with someone else")
        name: landing.name || null,
        // Who the scanner's sheet is about, before anything is recorded (E3b-2a)
        photoUrl: landing.photo_url || null,
        photoMeta: landing.photo_meta || null,
        colour: landing.colour || null,
      }
    : null;

  // PGRST116 guard kept for the multiple-rows edge (treated as no session)
  if (error && error.code !== 'PGRST116') {
    console.error('[ActiveSession] Lookup failed:', error);
    // Note: Don't log error here - let UserRedirect handle logging after all retries fail
    // This prevents duplicate error entries for the same underlying failure
    throw error;
  }

  if (data) {
    // Freshness gate: an is_active row whose heartbeat stopped
    // >= SESSION_FRESHNESS_MS ago is a dead session left by an unclean close
    // (app killed, tab discarded). The prefetch fast path already rejects
    // these with the same strict bound (updated_at strictly newer than
    // now-5min); without the gate here, this function's two callers
    // (UserRedirect fallback + the PXP guest path) adopt the stale room —
    // the wrong-room-join class (docs/fixes/.archived/pxp-wrong-room-join.md).
    // Checked client-side rather than in the query so the rejection is
    // loggable as distinct from "no session".
    const ageMs = Date.now() - new Date(data.updated_at).getTime();
    if (!(ageMs < SESSION_FRESHNESS_MS)) {
      // NaN age (unparseable updated_at) is treated as stale, not fresh
      console.log('[ActiveSession] Session is stale — treating as no session:', {
        profileUserId,
        ageMs,
      });
      logConnectionError('session_stale', { profileUserId, ageMs });
      return null;
    }
    console.log('[ActiveSession] Found active session:', {
      profileUserId,
      chatRoomId: data.chat_room_id,
    });
  } else {
    console.log('[ActiveSession] No active session for:', profileUserId);
  }

  return data; // null if no active session
};

/**
 * Look up an existing connection between an Anon user and a Profile user.
 * Used by UserRedirect when localStorage is stale/missing but the connection
 * exists in the DB. Returns the most recent active connection's chat_room_id.
 *
 * @param {string} guestUserId - The Anon user's ID (anon_XXXXXXXX)
 * @param {string} hostUserId - The Profile user's permanent ID (nanoid(6))
 * @returns {Promise<object|null>} - { chat_room_id } or null if no connection exists
 */
export const getExistingConnection = async (guestUserId, hostUserId) => {
  if (!supabase.from) {
    console.warn('[ActiveSession] Supabase client not initialized - cannot lookup connection');
    return null;
  }

  const { data, error } = await supabase
    .from('connections')
    .select('chat_room_id')
    .eq('guest_user_id', guestUserId)
    .eq('host_user_id', hostUserId)
    .eq('is_active', true)
    // Per-side delete (CD #15): never resurface a connection either side left —
    // a re-scan forms a fresh room (two cards for v1).
    .is('host_left_at', null)
    .is('guest_left_at', null)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('[ActiveSession] Connection lookup failed:', error);
    return null;
  }

  if (data) {
    console.log('[ActiveSession] Found existing connection:', {
      guestUserId,
      hostUserId,
      chatRoomId: data.chat_room_id,
    });
  }

  return data;
};

/**
 * Bidirectional duplicate detection — checks if two users already have a connection.
 * Used by PXP exchange (both Host and Guest) before any data writes.
 *
 * Three outcomes:
 *   - Returns chatRoomId string → reconnection (existing connection found)
 *   - Returns null → no existing connection (first connection)
 *   - Throws error → detection failed (caller must handle)
 *
 * @param {string} userA - First user's profileUserId
 * @param {string} userB - Second user's profileUserId
 * @param {string|null} anonUserId - Optional anonUserId for pre-conversion detection
 * @returns {Promise<string|null>} - Existing chatRoomId or null
 */
export const findExistingConnection = async (userA, userB, anonUserId = null) => {
  const { data, error } = await supabase.rpc('find_existing_connection', {
    p_user_a: userA,
    p_user_b: userB,
    p_anon_user_id: anonUserId,
  });
  if (error) throw error; // Caller must handle — see plan Step 5
  return data?.[0]?.chat_room_id ?? null;
};

// ============================================================
// Permanent Connections Table
// ============================================================
// The `connections` table is the SOLE AUTHORITY on whether a
// connection is confirmed. See docs/plans/connection-confirmation-single-source.md

/**
 * Record Guest-side confirmation in the connections table.
 * Called by Anon (alongside markAnonConnected) and PXP Guest (alongside profile_exchange send).
 * Fire-and-forget — does not block the existing flow.
 *
 * This is the FOUNDATION of missed-connection recovery. If this write fails,
 * there's no `connections` row for recovery to find. Retries with backoff.
 *
 * @param {string} chatRoomId
 * @param {string} hostUserId - The QR displayer's profileUserId
 * @param {string} guestUserId - The QR scanner's ID (anonUserId or profileUserId)
 * @param {string} exchangeType - 'anon_profile' or 'pxp'
 */
export const confirmGuestConnection = async (chatRoomId, hostUserId, guestUserId, exchangeType) => {
  const MAX_RETRIES = 3;
  const BACKOFF_MS = [0, 1000, 2000]; // immediate, 1s, 2s

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    if (attempt > 0) {
      await new Promise((r) => setTimeout(r, BACKOFF_MS[attempt]));
      console.log(`[ACTIVE_SESSION] confirmGuestConnection retry ${attempt}/${MAX_RETRIES - 1}`);
    }

    const { error } = await supabase.rpc('confirm_guest_connection', {
      p_chat_room_id: chatRoomId,
      p_host_user_id: hostUserId,
      p_guest_user_id: guestUserId,
      p_exchange_type: exchangeType,
      p_source_branch: SOURCE_BRANCH,
    });

    if (!error) return; // Success

    console.error(`[ACTIVE_SESSION] confirmGuestConnection attempt ${attempt + 1} failed:`, error);

    if (attempt === MAX_RETRIES - 1) {
      logConnectionError('database', {
        operation: 'confirm_guest_connection',
        code: error.code,
        message: error.message || 'All retries failed',
        chatRoomId,
      });
    }
  }
};

/**
 * Find orphaned connections — Guest confirmed but Host never detected.
 * Used by ProfileConnectionsListPage to recover missed connections.
 *
 * Returns connections where:
 * - host_user_id matches the Profile user
 * - guest_confirmed_at IS NOT NULL (Anon connected)
 * - host_confirmed_at IS NULL (Profile never detected)
 * - is_active is not false (not soft-deleted)
 *
 * @param {string} profileUserId - The Profile user's permanent ID
 * @returns {Promise<Array>} - Orphaned connection rows (chat_room_id, guest_confirmed_at, exchange_type)
 */
export const getOrphanedConnections = async (profileUserId) => {
  if (!supabase.from) return [];

  const { data, error } = await supabase
    .from('connections')
    .select('chat_room_id, guest_confirmed_at, exchange_type')
    .eq('host_user_id', profileUserId)
    .not('guest_confirmed_at', 'is', null)
    .is('host_confirmed_at', null)
    .or('is_active.is.null,is_active.eq.true')
    // Per-side delete (CD #15): orphan recovery must not resurrect a connection
    // either side left.
    .is('host_left_at', null)
    .is('guest_left_at', null);

  if (error) {
    console.error('[ACTIVE_SESSION] Failed to query orphaned connections:', error);
    return [];
  }

  return data || [];
};

/**
 * Host side of a profile-to-profile exchange: record the scanner as this room's
 * guest, so they can read the reply under the open-access lockdown's
 * participant-only rule. The server accepts only the id whose own signed-in
 * session wrote a card in this room (record_pxp_guest, migration 042).
 * @param {string} chatRoomId
 * @param {string} guestUserId - the scanner's profile id (from their card)
 * @returns {Promise<boolean>} true when recorded
 */
export const recordPxpGuest = async (chatRoomId, guestUserId) => {
  if (!chatRoomId || !guestUserId) return false;
  const { data, error } = await supabase.rpc('record_pxp_guest', {
    p_chat_room_id: chatRoomId,
    p_guest_user_id: guestUserId,
  });
  if (error) {
    console.error('[ACTIVE_SESSION] recordPxpGuest failed:', error);
    logConnectionError('database', {
      operation: 'record_pxp_guest',
      code: error.code,
      message: error.message,
      chatRoomId,
    });
    return false;
  }
  return data === true;
};

/**
 * Record Host-side confirmation in the connections table.
 * Called by Profile in handleAnonDetected() and PXP Host in profile_exchange handler.
 * Fire-and-forget — does not block the existing flow.
 *
 * @param {string} chatRoomId
 * @param {string} hostUserId - The QR displayer's profileUserId
 * @param {string} exchangeType - 'anon_profile' or 'pxp'
 */
export const confirmHostConnection = async (chatRoomId, hostUserId, exchangeType) => {
  const MAX_RETRIES = 3;
  const BACKOFF_MS = [0, 1000, 2000]; // immediate, 1s, 2s

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    if (attempt > 0) {
      await new Promise((r) => setTimeout(r, BACKOFF_MS[attempt]));
      console.log(`[ACTIVE_SESSION] confirmHostConnection retry ${attempt}/${MAX_RETRIES - 1}`);
    }

    const { error } = await supabase.rpc('confirm_host_connection', {
      p_chat_room_id: chatRoomId,
      p_host_user_id: hostUserId,
      p_exchange_type: exchangeType,
      p_source_branch: SOURCE_BRANCH,
    });

    if (!error) return; // Success

    console.error(`[ACTIVE_SESSION] confirmHostConnection attempt ${attempt + 1} failed:`, error);

    if (attempt === MAX_RETRIES - 1) {
      logConnectionError('database', {
        operation: 'confirm_host_connection',
        code: error.code,
        message: error.message || 'All retries failed',
        chatRoomId,
      });
    }
  }
};

/**
 * Lightweight check: has the Host confirmed this connection?
 * Used by Anon-side confirmation poll (replaces 15s timer).
 *
 * Returns three states:
 *   true  — Host confirmed (host_confirmed_at is set)
 *   false — Query succeeded but no confirmation yet
 *   null  — Query failed (network error, timeout) — inconclusive
 *
 * @param {string} chatRoomId
 * @returns {Promise<boolean|null>}
 */
export const checkHostConfirmed = async (chatRoomId) => {
  const { data, error } = await supabase
    .from('connections')
    .select('host_confirmed_at')
    .eq('chat_room_id', chatRoomId)
    .maybeSingle();

  if (error) return null; // Query failed — inconclusive
  if (!data) return false; // No row yet — not confirmed
  return !!data.host_confirmed_at;
};

/**
 * Whether this profile hosts the room — the host's own check on a returning guest's
 * room (mark_guest_returned already verified the guest server-side; the host never
 * acts on a room id it can't see as its own).
 * @returns {Promise<boolean>}
 */
export const isHostOfRoom = async (chatRoomId, profileUserId) => {
  if (!chatRoomId || !profileUserId) return false;
  const { data, error } = await supabase
    .from('connections')
    .select('chat_room_id')
    .eq('chat_room_id', chatRoomId)
    .eq('host_user_id', profileUserId)
    .eq('is_active', true)
    .maybeSingle();
  return !error && !!data;
};

/**
 * The room's recorded guest — the id the server kept as `guest_user_id` (first
 * guest wins: confirm_guest_connection never hands the seat to another phone).
 * @returns {Promise<string|null>} null when there is no row yet or the read failed
 */
export const getRoomGuest = async (chatRoomId) => {
  if (!chatRoomId) return null;
  const { data, error } = await supabase
    .from('connections')
    .select('guest_user_id')
    .eq('chat_room_id', chatRoomId)
    .maybeSingle();
  if (error) {
    logConnectionError('database', {
      operation: 'room_guest_read',
      code: error.code,
      message: error.message,
      chatRoomId,
    });
    return null;
  }
  return data?.guest_user_id || null;
};

/**
 * Full connection status check.
 * Used for status dot on connection page and deleted-connection detection.
 *
 * @param {string} chatRoomId
 * @returns {Promise<'confirmed'|'unconfirmed'|'deleted'>}
 */
export const getConnectionStatus = async (chatRoomId) => {
  const { data, error } = await supabase
    .from('connections')
    .select('host_confirmed_at, guest_confirmed_at, is_active')
    .eq('chat_room_id', chatRoomId)
    .maybeSingle();

  if (error || !data) return 'unconfirmed';
  if (!data.is_active) return 'deleted';
  if (data.host_confirmed_at && data.guest_confirmed_at) return 'confirmed';
  return 'unconfirmed';
};

/**
 * Batched profile lookup for the People list.
 * Fetches current name and photo for all connected Profile users in a single query.
 * Resolves converted anon IDs via profiles.converted_from_anon_id JOIN.
 *
 * @param {string} profileUserId - The current user's profile ID
 * @returns {Promise<Array>} - Array of { chat_room_id, name, photo_url }
 */
export const getConnectionProfiles = async (profileUserId) => {
  if (!supabase.from) return [];

  const { data, error } = await supabase.rpc('get_connection_profiles', {
    p_user_id: profileUserId,
  });

  if (error) {
    console.error('[ACTIVE_SESSION] Failed to fetch connection profiles:', error.message);
    return [];
  }

  return data || [];
};
